import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import {
  WHATS_NEW_COLORS,
  WHATS_NEW_FILTERS,
  WHATS_NEW_UPDATE_TILE_COLORS,
  findWhatsNewApp,
  getRelativeLabel,
  groupEntriesByMonth,
  hashString
} from 'utils/whatsNew';

/**
 * The What's New page (Figma 408:2) — web port.
 *
 * Content is entirely admin-authored: the app renders whatever /whats_new
 * returns and never derives entries from git history, so adding a future
 * announcement requires no code change.
 *
 * `LogoComponent` is injected because the cloud dashboard defines ToolLogo
 * inline; passing it avoids a circular import.
 */

function FilterChips({ activeFilter, onChange }) {
  return (
    <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
      {WHATS_NEW_FILTERS.map((filter) => {
        const isSelected = activeFilter === filter.value;
        return (
          <Box
            key={filter.value}
            component="button"
            type="button"
            onClick={() => onChange(filter.value)}
            sx={{
              minWidth: 92,
              height: 32,
              px: 2,
              borderRadius: '8px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              outline: 'none',
              color: isSelected ? 'white' : '#aeb6c2',
              backgroundColor: isSelected ? '#3a54c0' : '#121a2a',
              border: '1px solid',
              borderColor: isSelected ? '#3a54c0' : '#222c3e',
              transition: 'background-color 140ms ease, color 140ms ease',
              '&:hover': { backgroundColor: isSelected ? '#3a54c0' : '#161e2e', color: 'white' },
              '&:focus-visible': { boxShadow: '0 0 0 2px rgba(112, 141, 255, 0.5)' }
            }}
          >
            {filter.label}
          </Box>
        );
      })}
    </Stack>
  );
}

function TypePill({ type }) {
  const isUpdate = type === 'update';

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 24,
        px: 1.25,
        borderRadius: '12px',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.3px',
        backgroundColor: isUpdate ? WHATS_NEW_COLORS.updatePillBg : WHATS_NEW_COLORS.newPillBg,
        border: '1px solid',
        borderColor: isUpdate ? WHATS_NEW_COLORS.updatePillBorder : WHATS_NEW_COLORS.newPillBorder,
        color: isUpdate ? WHATS_NEW_COLORS.updatePillText : WHATS_NEW_COLORS.newPillText,
        alignSelf: 'flex-start'
      }}
    >
      {isUpdate ? 'UPDATE' : 'NEW'}
    </Box>
  );
}

// New-tool entries show the real tool logo; update entries get a stable accent
// tile, since they aren't tied to a tool.
function EntryIcon({ entry, linkedApp, LogoComponent }) {
  if (entry.type !== 'update' && linkedApp && LogoComponent) {
    return <LogoComponent app={linkedApp} size={44} radius={10} />;
  }

  const palette = WHATS_NEW_UPDATE_TILE_COLORS;
  const color = palette[hashString(entry.title) % palette.length];

  return (
    <Box
      aria-hidden
      sx={{
        width: 54,
        height: 54,
        borderRadius: '12px',
        backgroundColor: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: 18,
        fontWeight: 700,
        flexShrink: 0
      }}
    >
      {entry.type === 'update' ? 'U' : (entry.title[0] || '?').toUpperCase()}
    </Box>
  );
}

function EntryRow({ entry, linkedApp, onLaunchApp, onOpenExternal, LogoComponent }) {
  const canLaunch = entry.type !== 'update' && Boolean(linkedApp);
  const canLearnMore = Boolean(entry.learn_more_url);
  const relativeLabel = getRelativeLabel(entry);

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      alignItems={{ xs: 'flex-start', md: 'center' }}
      spacing={{ xs: 1.5, md: 2.5 }}
      sx={{
        px: 2.5,
        py: 2,
        borderRadius: '12px',
        backgroundColor: WHATS_NEW_COLORS.rowBg,
        border: `1px solid ${WHATS_NEW_COLORS.rowBorder}`
      }}
    >
      <EntryIcon entry={entry} linkedApp={linkedApp} LogoComponent={LogoComponent} />

      <Stack spacing={0.75} sx={{ flexGrow: 1, minWidth: 0 }}>
        <TypePill type={entry.type} />
        <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'white' }}>{entry.title}</Typography>
        {entry.description ? <Typography sx={{ fontSize: 12, color: '#aeb6c2' }}>{entry.description}</Typography> : null}
      </Stack>

      {relativeLabel ? (
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#8b94a5', flexShrink: 0, whiteSpace: 'nowrap' }}>{relativeLabel}</Typography>
      ) : null}

      {/* Update entries may legitimately have no CTA (Figma 410:2). */}
      {canLaunch || canLearnMore ? (
        <Box
          component="button"
          type="button"
          onClick={() => (canLaunch ? onLaunchApp(linkedApp) : onOpenExternal(entry.learn_more_url))}
          sx={{
            width: { xs: '100%', md: 140 },
            height: 40,
            flexShrink: 0,
            borderRadius: '9px',
            fontSize: canLaunch ? 13 : 12,
            fontWeight: 600,
            color: 'white',
            cursor: 'pointer',
            outline: 'none',
            backgroundColor: canLaunch ? '#3a54c0' : WHATS_NEW_COLORS.learnMoreBg,
            border: '1px solid',
            borderColor: canLaunch ? '#3a54c0' : WHATS_NEW_COLORS.learnMoreBorder,
            transition: 'background-color 140ms ease',
            '&:hover': { backgroundColor: canLaunch ? '#314aad' : '#1a2336' },
            '&:focus-visible': { boxShadow: '0 0 0 2px rgba(112, 141, 255, 0.5)' }
          }}
        >
          {canLaunch ? 'Launch' : 'Learn more ↗'}
        </Box>
      ) : null}
    </Stack>
  );
}

export default function WhatsNewPage({
  entries,
  appList,
  loading,
  error,
  activeFilter,
  onChangeFilter,
  onLaunchApp,
  onOpenExternal,
  LogoComponent
}) {
  const safeEntries = Array.isArray(entries) ? entries : [];

  const visibleEntries = useMemo(
    () => (activeFilter === 'all' ? safeEntries : safeEntries.filter((entry) => entry.type === activeFilter)),
    [safeEntries, activeFilter]
  );

  const sections = useMemo(() => groupEntriesByMonth(visibleEntries), [visibleEntries]);

  return (
    <Stack spacing={3} sx={{ pb: 2 }}>
      <FilterChips activeFilter={activeFilter} onChange={onChangeFilter} />

      {loading && safeEntries.length === 0 ? <Typography sx={{ fontSize: 14, color: '#8b94a5' }}>Loading updates…</Typography> : null}

      {error && safeEntries.length === 0 ? <Typography sx={{ fontSize: 14, color: '#e2555f' }}>{error}</Typography> : null}

      {!loading && !error && visibleEntries.length === 0 ? (
        <Typography sx={{ fontSize: 14, color: '#8b94a5' }}>
          Nothing here yet. New tools and product updates will show up on this page.
        </Typography>
      ) : null}

      {sections.map((section) => (
        <Stack key={section.label} spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              aria-hidden
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: '#18213c',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: WHATS_NEW_COLORS.sparkle,
                fontSize: 14,
                fontWeight: 700,
                flexShrink: 0
              }}
            >
              &#10022;
            </Box>
            <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'white' }}>{section.label}</Typography>
          </Stack>

          <Stack spacing={1.25}>
            {section.entries.map((entry) => (
              <EntryRow
                key={entry.id}
                entry={entry}
                linkedApp={findWhatsNewApp(entry, appList)}
                onLaunchApp={onLaunchApp}
                onOpenExternal={onOpenExternal}
                LogoComponent={LogoComponent}
              />
            ))}
          </Stack>
        </Stack>
      ))}

      {visibleEntries.length > 0 ? (
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.25}>
          <Box aria-hidden sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: WHATS_NEW_COLORS.sparkle, opacity: 0.7 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#7c8597' }}>
            More updates coming soon. Thanks for being part of KLOOW.
          </Typography>
        </Stack>
      ) : null}
    </Stack>
  );
}
